import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
	Table,
	TableHeader,
	TableBody,
	TableHead,
	TableRow,
	TableCell,
	Button,
	Badge,
	Spinner,
	EmptyState,
	PlatformMark
} from '../ui';
import { ClientActionsDropdown } from './ClientActionsDropdown';
import { PersonIcon } from '@radix-ui/react-icons';
import { PATHS } from '../../constants/Paths';
import { cn } from '../../utils/cn';

const STALE_DAYS = 14;

/**
 * ClientsTable - the roster a manager scans in one pass.
 *
 * Rebuilt around the question the page exists to answer: "which of my people
 * needs me, and what do I do about it?" Every column now earns its width.
 * Removed: the Category column (it read "Individual" for effectively every
 * client), the email line (managers know their own clients), and a duplicate
 * risk representation - the row showed "74%" AND a "High" badge side by side.
 *
 * Added: the platform marks the client is actually monitored on, staleness on
 * the last scan, and a primary Scan action. Scanning is the daily job; the old
 * row's one prominent button was "+ Add Account", a once-per-account setup
 * task, while the daily action took three clicks through the detail page.
 */
export const ClientsTable = ({
	clients = [],
	onAddAccount,
	loading = false,
	className
}) => {
	const navigate = useNavigate();

	const formatDate = (date) => {
		if (!date) return "Never";
		return new Date(date).toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric",
		});
	};

	const staleDays = (date) => {
		if (!date) return null;
		return Math.floor((Date.now() - new Date(date).getTime()) / 86400000);
	};

	const riskVariant = (risk) => {
		if (risk >= 70) return "destructive";
		if (risk >= 40) return "warning";
		return "success";
	};

	const riskLabel = (risk) => {
		if (risk >= 70) return "High";
		if (risk >= 40) return "Watch";
		return "Clear";
	};

	const handleViewClient = (clientId) => navigate(`/clients/${clientId}`);

	// Prefills the scan form with this client; the manager picks the account.
	const handleScanClient = (clientId) =>
		navigate(PATHS.NEW_SCANS, { state: { clientId } });

	/** Platform marks for the client's accounts, deduped. */
	const PlatformSet = ({ accounts = [] }) => {
		const platforms = [...new Set(accounts.map((a) => a.platform).filter(Boolean))];
		if (platforms.length === 0) {
			return <span className="text-xs text-muted-foreground">No accounts</span>;
		}
		return (
			<div className="flex items-center gap-1.5">
				{platforms.map((p) => <PlatformMark key={p} platform={p} size="xs" />)}
				<span className="text-sm text-muted-foreground">{accounts.length}</span>
			</div>
		);
	};

	/** Last scan with the coverage-gap signal - silently unmonitored is a risk state. */
	const LastScan = ({ date }) => {
		const days = staleDays(date);
		const stale = days === null || days > STALE_DAYS;
		return (
			<span className={cn("text-sm", stale ? "font-medium text-amber-600" : "text-muted-foreground")}>
				{formatDate(date)}
				{stale && days !== null && <span className="ml-1 text-xs">· {days}d</span>}
			</span>
		);
	};

	if (loading) {
		return (
			<div className="w-full p-8 text-center">
				<Spinner size="lg" className="mx-auto" />
				<p className="mt-4 text-muted-foreground">Loading clients…</p>
			</div>
		);
	}

	if (clients.length === 0) {
		return (
			<EmptyState
				compact
				icon={PersonIcon}
				title="No clients yet"
				description="Add your first client to start monitoring their accounts."
			/>
		);
	}

	return (
		<div className={cn("space-y-3", className)}>
			{/* Mobile: cards */}
			<div className="space-y-3 md:hidden">
				{clients.map((client, index) => (
					<div
						key={client._id || index}
						onClick={() => handleViewClient(client._id)}
						className="rounded-xl border border-border bg-card p-4 cursor-pointer hover:border-input"
					>
						<div className="flex items-start justify-between gap-2">
							<div className="flex items-center gap-3 min-w-0">
								<img src={client.primaryAvatar || client.avatar || "https://placehold.co/40x40"} alt="" className="w-10 h-10 rounded-full object-cover bg-muted shrink-0" />
								<div className="min-w-0">
									<div className="font-medium text-foreground truncate">{client.name}</div>
									<div className="text-xs text-muted-foreground capitalize">{client.category || 'Individual'}</div>
								</div>
							</div>
							<Badge variant={riskVariant(client.risk)} size="sm">
								{riskLabel(client.risk)} · {client.risk || 0}
							</Badge>
						</div>
						<div className="mt-3 flex items-center justify-between gap-2">
							<PlatformSet accounts={client.accounts} />
							<LastScan date={client.lastScan} />
						</div>
						<div className="mt-3 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
							<Button size="sm" onClick={() => handleScanClient(client._id)}>Scan</Button>
							<ClientActionsDropdown client={client} onAddAccount={onAddAccount} />
						</div>
					</div>
				))}
			</div>

			{/* Desktop: table */}
			<div className="hidden md:block rounded-lg border border-border bg-card overflow-x-auto min-h-[16rem]">
				<Table>
					<TableHeader>
						<TableRow className="bg-muted">
							<TableHead className="min-w-[220px]">Client</TableHead>
							<TableHead>Monitored on</TableHead>
							<TableHead>Risk</TableHead>
							<TableHead>Last scan</TableHead>
							<TableHead className="text-right">Actions</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{clients.map((client, index) => (
							<TableRow
								key={client._id || index}
								className="hover:bg-muted cursor-pointer"
								onClick={() => handleViewClient(client._id)}
							>
								<TableCell>
									<div className="flex items-center gap-3">
										<img
											src={client.primaryAvatar || client.avatar || "https://placehold.co/40x40"}
											alt=""
											className="w-9 h-9 rounded-full object-cover bg-muted shrink-0"
										/>
										<div className="min-w-0">
											<div className="font-medium text-foreground truncate">{client.name}</div>
											<div className="text-xs text-muted-foreground capitalize">{client.category || 'Individual'}</div>
										</div>
									</div>
								</TableCell>

								<TableCell><PlatformSet accounts={client.accounts} /></TableCell>

								<TableCell>
									<Badge variant={riskVariant(client.risk)} size="sm">
										{riskLabel(client.risk)} · {client.risk || 0}
									</Badge>
								</TableCell>

								<TableCell><LastScan date={client.lastScan} /></TableCell>

								<TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
									<div className="flex items-center gap-2 justify-end">
										<Button size="sm" onClick={() => handleScanClient(client._id)}>Scan</Button>
										<ClientActionsDropdown client={client} onAddAccount={onAddAccount} />
									</div>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>
		</div>
	);
};
